/**
 * CR306/CR307: Theme Preview Wrapper
 * 
 * Isolated wrapper that applies theme tokens as CSS variables
 * to the preview area without affecting the admin UI.
 * 
 * This ensures instant preview updates from draft tokens
 * while keeping styles scoped to the preview container.
 * 
 * CR307 Enhancements:
 * - Debug data attributes for var count and hash
 * - Memoization with proper deps to prevent stale refs
 */

import { useMemo, type ReactNode } from "react";
import type { GlobalThemeTokens } from "@/lib/deals/globalThemeTokens";
import { resolveGlobalThemeCssVars } from "@/lib/deals/themeTokensCssResolver";
import { cn } from "@/lib/utils";

interface ThemePreviewWrapperProps {
  /** Theme tokens to apply (draft or persisted) */
  tokens: GlobalThemeTokens;
  /** Additional className for the wrapper */
  className?: string;
  /** Children to render inside the themed wrapper */
  children: ReactNode;
  /** Optional data attribute for debugging */
  dataSource?: "draft" | "persisted";
  /** Optional debug mode - adds extra data attributes */
  debug?: boolean;
}

/**
 * Compute a simple hash for token comparison/debugging
 */
function computeTokenHash(tokens: GlobalThemeTokens): string {
  const str = JSON.stringify(tokens);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

export function ThemePreviewWrapper({
  tokens,
  className,
  children,
  dataSource = "draft",
  debug = false,
}: ThemePreviewWrapperProps) {
  // CR307: CRITICAL - Always create a NEW object to ensure React re-renders
  // The JSON parse/stringify ensures we're not reusing memoized references
  const cssVars = useMemo(() => {
    // Force fresh object creation - this is critical for React to see changes
    const freshTokens = JSON.parse(JSON.stringify(tokens)) as GlobalThemeTokens;
    return resolveGlobalThemeCssVars(freshTokens);
  }, [tokens]);

  // Count vars for debug
  const varCount = Object.keys(cssVars).length;
  
  // Hash for debug
  const tokenHash = useMemo(() => computeTokenHash(tokens), [tokens]);

  return (
    <div
      className={cn(
        "theme-preview-wrapper",
        // Ensure isolation from admin styles
        "relative isolate",
        className
      )}
      style={cssVars as React.CSSProperties}
      data-theme-source={dataSource}
      data-theme-hash={tokenHash}
      data-theme-var-count={varCount}
      {...(debug && {
        "data-theme-debug": "true",
      })}
    >
      {/* CR307: Debug comment for DOM inspection */}
      {/* <!-- ThemePreviewWrapper: {varCount} CSS vars applied, hash={tokenHash} --> */}
      {children}
    </div>
  );
}
